import { AxiosPromise, AxiosResponse } from 'axios';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import MojoApiFactory from '../config/api-factory';
import {
  ChatbotSendMessageRequest,
  ChatbotSendMessageResponse,
  ChatbotThread,
  ChatbotThreadListResponse,
  ChatbotThreadMessagesResponse,
  ChatbotResumeResponse,
  ChatbotSearchResponse,
  StreamEvent,
  ReferenceCardResponse,
  SuggestionsResponse,
} from '../models/chatbot/types';

// Capability negotiation constants
const LINK_FORMAT = 'universal';
const SUPPORTED_CARD_TYPES = 'landscape';

export const getChatThreadMessages = async (threadId: string) => {
  return (await MojoApiFactory.chatbot.get(
    `chat_messages?threadId=${threadId}&link_format=${LINK_FORMAT}&card_types=${SUPPORTED_CARD_TYPES}`
  )) as AxiosResponse<ChatbotThreadMessagesResponse>;
};

export const getChatThreadList = async (
  page = 1,
  perPage = 20,
  archived?: boolean,
  source?: 'chat' | 'search'
) => {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
  });
  if (archived !== undefined) {
    params.set('archived', String(archived));
  }
  if (source !== undefined) {
    params.set('source', source);
  }
  return (await MojoApiFactory.chatbot.get(
    `chat_threads?${params.toString()}`
  )) as AxiosResponse<ChatbotThreadListResponse>;
};

export const resumeChatThread = async () => {
  return (await MojoApiFactory.chatbot.get(
    'chat_threads/resume'
  )) as AxiosResponse<ChatbotResumeResponse>;
};

export const updateChatThread = async (
  threadId: string,
  data: {
    title?: string;
    titleManuallySet?: boolean;
    archived?: boolean;
    source?: 'chat' | 'search';
  }
) => {
  return (await MojoApiFactory.chatbot.patch(
    `chat_threads/${threadId}`,
    data
  )) as AxiosResponse<{ data: ChatbotThread }>;
};

export const searchChatThreads = async (
  query: string,
  page = 1,
  perPage = 20
) => {
  const params = new URLSearchParams({
    q: query,
    page: String(page),
    perPage: String(perPage),
  });
  return (await MojoApiFactory.chatbot.get(
    `chat_threads/search?${params.toString()}`
  )) as AxiosResponse<ChatbotSearchResponse>;
};

export const sendMessage = async ({
  threadId,
  message,
}: ChatbotSendMessageRequest) => {
  // Use new chat message batch endpoint that creates both user and assistant messages
  return MojoApiFactory.chatbot.post(`chat_messages/completions`, {
    threadId,
    content: message,
    link_format: LINK_FORMAT,
    card_types: SUPPORTED_CARD_TYPES,
  }) as AxiosPromise<ChatbotSendMessageResponse>;
};

export const sendMessageStream = async ({
  threadId,
  message,
  onEvent,
  signal,
}: ChatbotSendMessageRequest & {
  onEvent: (event: StreamEvent) => void;
  signal?: AbortSignal;
}): Promise<void> => {
  fetchEventSource(
    `${MojoApiFactory.chatbot.defaults.baseURL}chat_messages/completions`,
    {
      openWhenHidden: true, // Keep SSE alive when tab is hidden to prevent reconnect/retry
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        // Include authentication headers from the axios instance
        ...(MojoApiFactory.chatbot.defaults.headers.common as Record<
          string,
          string
        >),
      },
      body: JSON.stringify({
        content: message,
        threadId,
        link_format: LINK_FORMAT,
        card_types: SUPPORTED_CARD_TYPES,
      }),
      onmessage(event: any) {
        if (event.event === 'ping') return; // Skip empty ping events

        try {
          const data = JSON.parse(event.data);
          const eventType = event.event || 'message_update';
          onEvent({
            type: eventType as StreamEvent['type'],
            data,
          } as StreamEvent);
        } catch (e) {
          console.warn('Failed to parse SSE data:', event.data);
        }
      },
      onerror(error: any) {
        onEvent({ type: 'error', data: { error: error.message || error } });
        throw error; // Stop retrying
      },
    }
  );
};

export const getReferenceCard = async (
  referenceType: string,
  referenceId: string
) => {
  return (await MojoApiFactory.chatbot.get(
    `reference_cards/${referenceType}/${referenceId}?link_format=${LINK_FORMAT}&card_types=${SUPPORTED_CARD_TYPES}`
  )) as AxiosResponse<ReferenceCardResponse>;
};

export const getSuggestions = async () => {
  return (await MojoApiFactory.chatbot.get(
    'suggestions'
  )) as AxiosResponse<SuggestionsResponse>;
};
